import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { TelemetryProvider } from '../telemetry/TelemetryProvider.js';
import { lazy, Suspense, useEffect } from 'react';
import { AppShell } from '../AppShell.js';
import { BrandSync } from '../BrandSync.js';

/**
 * Code-first route tree, screens behind React.lazy.
 *
 * Two layouts, not one (issue #25). `/` is the PUBLIC front door and renders
 * its own chrome — a marketing page must not carry the signed-in header or the
 * dev identity switcher — so it hangs off the root beside a pathless layout
 * route that supplies `AppShell` to everything else. The learner dashboard
 * moved from `/` to `/courses` to make room; nothing links to it by row id, so
 * the move is confined to this file and the post-login destination table in
 * `@cusp/api-client`.
 *
 * Still NO auth guard on `/courses`: outside production the header strategy
 * works with no session at all, so "no identity yet" stays an empty state
 * rather than a redirect — and an institution admin is *landed* in the admin
 * app after login, not *barred* from their own courses. Routing by role happens
 * at the point of sign-in (`LoginScreen`), which is what #25 specifies.
 */

const CoursesScreen = lazy(() =>
  import('../screens/CoursesScreen.js').then((m) => ({ default: m.CoursesScreen })),
);
const NotFoundScreen = lazy(() =>
  import('../screens/NotFoundScreen.js').then((m) => ({ default: m.NotFoundScreen })),
);
const LoginScreen = lazy(() =>
  import('../screens/LoginScreen.js').then((m) => ({ default: m.LoginScreen })),
);
const LandingScreen = lazy(() =>
  import('../screens/LandingScreen.js').then((m) => ({ default: m.LandingScreen })),
);

/**
 * Bare root: the lazy boundary every layout needs, the engagement beacon, the
 * brand syncer, and nothing else.
 *
 * The beacon lives HERE rather than in `main.tsx` because two of the three
 * signals it reports are router facts — a page view is a route change, and a
 * visit is the span across them — and because it must sit inside the
 * QueryClientProvider that `main.tsx` mounts around this tree. It reports
 * nothing at all until an identity exists.
 *
 * THE BRAND SYNCER IS HERE FOR THE OPPOSITE REASON TO THE BEACON: not because
 * it needs the router, but because there is exactly one of it and this is the
 * only component every surface in the bundle passes through. `useBrandSync`
 * asks for one instance per app, and this app has three top-level layouts with
 * no shared authenticated shell — one syncer per layout would put two live at
 * once during a route swap. `BrandSync` reads the location itself and suppresses
 * the PAINT on `/` and `/login` — the tenant still goes through, so the school's
 * default direction survives the front door — which is what keeps the marketing
 * page Cusp gold for a signed-in visitor.
 */
const rootRoute = createRootRoute({
  component: () => (
    <TelemetryProvider>
      <BrandSync />
      <Suspense fallback={<p>Loading…</p>}>
        <Outlet />
      </Suspense>
    </TelemetryProvider>
  ),
});

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingScreen,
});

/**
 * `/internal-demo` — the learner design walkthrough (#20's static-first pass).
 *
 * Hangs off the ROOT, beside the landing page rather than inside `AppShell`:
 * the learner screens carry their own masthead, and the dev identity switcher
 * above them would be a second, contradictory chrome.
 *
 * DELIBERATELY SHIPPED IN PRODUCTION (Pete, 2026-08-08) so the walkthrough can
 * be shown to people while the real screens are worked out. This reverses the
 * original dev-only gate, and it is only defensible because the walkthrough
 * now labels itself: a fixture banner on every screen, every unbuilt control
 * disabled with a visible reason, and no control that lies. If any of that is
 * ever removed, the `import.meta.env.PROD ? [] : [...]` gate goes back —
 * unlabelled fixture data on the real domain is indistinguishable from a
 * broken product.
 *
 * "internal-" in the path is the audience statement: reachable by URL, linked
 * from nowhere. The route lazy-loads, so the fixtures stay out of the landing
 * page's initial bundle either way. Deleted with `DemoScreen` + `src/demo/`
 * when #20's real screens land behind auth.
 */
const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/internal-demo',
  component: lazy(() =>
    import('../screens/DemoScreen.js').then((m) => ({ default: m.DemoScreen })),
  ),
});

/**
 * `/internal-admin` — the typeable address of the institution-console
 * walkthrough, `/internal-demo`'s admin sibling. The walkthrough itself lives
 * in apps/admin (entered at /admin/internal-demo — see that app's main.tsx),
 * but the edge serves the LEARNER bundle for every extensionless path outside
 * /admin, so the short memorable form has to land here and hop bundles with a
 * hard replace. Same audience statement: reachable by URL, linked from nowhere.
 */
const prospectDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo',
  component: lazy(() =>
    import('../screens/DemoScreen.js').then((m) => ({ default: m.DemoScreen })),
  ),
});

const internalAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/internal-admin',
  component: function InternalAdminRedirect() {
    useEffect(() => {
      window.location.replace('/admin/internal-demo');
    }, []);
    return <p>Opening the console walkthrough…</p>;
  },
});

/**
 * The wired learner screens (#20): L2 at `/courses/$courseSlug` and L3 at
 * `/courses/$courseSlug/lectures/$lectureKey`.
 *
 * Off the ROOT rather than inside `AppShell`, for the same reason
 * `/internal-demo` is: these screens carry their own masthead, and the shell's
 * header above it would be two contradictory chromes. What the shell supplies
 * that they still need — theme toggle and session controls — they mount
 * themselves through the masthead's `actions` slot (`learner/shell.tsx`).
 *
 * URLS CARRY STABLE KEYS ONLY: a course SLUG and a lecture KEY. Publishing a
 * new version regenerates `lectures.id` and `course_versions.id`, so a link
 * anyone bookmarked or pasted into Slack would rot on the next publish. The
 * version is not in the URL at all — it is the caller's `enrolled` version,
 * resolved server-side per request.
 */
const CourseScreen = lazy(() =>
  import('../screens/learner/CourseScreen.js').then((m) => ({ default: m.CourseScreen })),
);
const LectureScreen = lazy(() =>
  import('../screens/learner/LectureScreen.js').then((m) => ({ default: m.LectureScreen })),
);

/**
 * `/courses` — the learner's home (#104), wired to live data.
 *
 * Off the ROOT, beside the per-course screens rather than inside `AppShell`:
 * the wired home carries its own masthead (`LearnerActions` in its `actions`
 * slot), and the shell header above it would be two contradictory chromes —
 * the same reason `CourseScreen` hangs here. Still no auth guard: outside
 * production the header strategy works with no session, so "no identity yet"
 * stays an empty state rather than a redirect.
 */
const coursesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/courses',
  component: CoursesScreen,
});

const courseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/courses/$courseSlug',
  component: CourseScreen,
});

/**
 * `?tutor=<kind>&session=<id>` — RESUMING one CuspTutor conversation (#275).
 *
 * In the URL rather than in a router state blob, because it is a destination: a
 * learner clicks a row in their conversation history on the course page, lands
 * in the lecture, and that address is what a back button and a reload have to
 * mean. The id is the session's OWN row id (tenant-scoped learner data, never a
 * catalog id) and the server identity-checks it on read — a stranger's id is
 * 404 — so a typed one is harmless, and the panel that cannot adopt it says so
 * and offers a new room.
 *
 * VALIDATION IS A NARROWING, NOT A GUARD: anything unrecognised becomes
 * `undefined`, so a mangled link opens the lecture normally instead of erroring.
 */
const lectureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/courses/$courseSlug/lectures/$lectureKey',
  validateSearch: (search: Record<string, unknown>): TutorResumeSearch => {
    const kind = search['tutor'];
    const session = search['session'];
    return {
      ...(kind === 'practice' || kind === 'lecture_discussion' ? { tutor: kind } : {}),
      ...(typeof session === 'string' && session.length > 0 ? { session } : {}),
    };
  },
  component: LectureScreen,
});

export interface TutorResumeSearch {
  tutor?: 'practice' | 'lecture_discussion';
  session?: string;
}

/**
 * `/login` — off the ROOT, beside the landing page rather than inside
 * `AppShell`: the sign-in screen (`CUSP Sign In.dc.html`) carries its own
 * two-pane chrome, its own theme toggle and, outside production, its own dev
 * identity switcher. A shell header above it would be two contradictory
 * chromes — the same reason the landing page and the learner screens hang here.
 */
/**
 * `/tutor/how-it-works` — CuspTutor's Layer 3 disclosure (docs/tutor-contract.md
 * rules 56–61), the standing page the Layer 1 line under every composer and the
 * first-run card both link to.
 *
 * OFF THE ROOT and with NO AUTH GUARD, both deliberately. It carries the
 * learner masthead like the other learner screens, so the shell's header above
 * it would be two contradictory chromes; and a disclosure a reader has to sign
 * in to read is not a disclosure. Its URL is a constant in `@cusp/core`
 * (`TUTOR_DISCLOSURE_HOW_IT_WORKS_HREF`) — the same string the ratified copy
 * links to — so the path and the link cannot drift apart.
 */
const tutorHowItWorksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tutor/how-it-works',
  component: lazy(() =>
    import('../screens/learner/tutor/HowCuspTutorWorksScreen.js').then((m) => ({
      default: m.HowCuspTutorWorksScreen,
    })),
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

/** Pathless layout: what is left of the signed-in shell — `/welcome`. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AppShell,
});

/**
 * `/welcome` — the zero-membership landing (issue #90). A fresh Google account
 * holds no membership to route by, so `LoginScreen` sends it here; the screen
 * itself re-reads the session, so the URL is also safely typeable cold.
 */
const welcomeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/welcome',
  component: lazy(() =>
    import('../screens/WelcomeScreen.js').then((m) => ({ default: m.WelcomeScreen })),
  ),
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    landingRoute,
    demoRoute,
    prospectDemoRoute,
    internalAdminRoute,
    coursesRoute,
    courseRoute,
    lectureRoute,
    tutorHowItWorksRoute,
    loginRoute,
    shellRoute.addChildren([welcomeRoute]),
  ]),
  defaultNotFoundComponent: NotFoundScreen,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
